import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ViewChild } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Dictionary } from '@ngrx/entity';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { BrowserDimensions } from '@panamax/app-state/lib/models/platform.model';
import * as PaymentConstants from '@payments/constants/constants';
import { AdjustPaymentPageViewModel } from '@payments/models/adjust-payment-page-view-model';
import { TransactionRowText } from '@payments/models/payment-transaction';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import { TransactionRowDesktopConstants } from '@payments/payments-shared/transaction-row/desktop/transaction-row-desktop.component';
import { InvoiceIsrService } from '@payments/services/invoice-isr.service';
import { PaymentsService } from '@payments/services/payments.service';
import { QuickFilter } from '@shared/components/usf-filtering-banner/model/quick-filter.model';
import { UsfFilteringModalComponent } from '@shared/components/usf-filtering-modal/usf-filtering-modal.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { Filter, InvoiceFilterIds, SortDirection } from '@usf/ngrx-payment';
import {
  PaymentTransactionsViewModel,
  SortFields,
  Status,
} from '@usf/payment-types';
import { Observable, Subscription, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilKeyChanged, filter, take } from 'rxjs/operators';
import { AdjustPaymentService } from './services/adjust-payment.service';
import { AdjustPaymentAnalyticsService } from './tracking/analytics/adjust-payment-analytics.service';

@Component({
  selector: 'app-adjust-payment',
  templateUrl: './adjust-payment.page.html',
  styleUrls: ['./adjust-payment.page.scss'],
})
export class AdjustPaymentPage extends PaymentsBasePage {
  @ViewChild(CdkVirtualScrollViewport)
  virtualScrollViewport: CdkVirtualScrollViewport;

  readonly PlatformEnum = PlatformEnum;
  readonly SortDirection = SortDirection;
  readonly SortFields = SortFields;
  readonly InvoiceFilterIds = InvoiceFilterIds;
  readonly ColumnNames = TransactionRowDesktopConstants.ColumnNames;

  amountIsHigherErrorMessage: string;
  amountIsZeroErrorMessage: string;
  browserDimensions: BrowserDimensions;
  getFiltersSub$: Subscription;
  invoiceFilters: Filter[];
  isMobile: boolean;
  isInitialLoading = true;
  isTouch$: Observable<boolean>;
  loadingSub$: Subscription;
  maxTransactionsSelectedMessage: string;
  modal: UsfFilteringModalComponent;
  noSelectedTransactionsSub$: Subscription;
  quickFiltersSub$: Subscription;
  selectedCustomersSub$: Subscription;
  selectedSortDirection: SortDirection;
  selectedSortField: SortFields;
  sortDirection: SortDirection;
  sortField: SortFields;
  selectedTransactions$: Observable<PaymentTransactionsViewModel[]>;
  selectedTransactionsSub$: Subscription;
  transactionRowText: TransactionRowText;
  warningMessageBanner: string;
  vm$: Observable<AdjustPaymentPageViewModel>;
  excludedInvoicesIds: string[];
  paymentCustomers$: Subscription;
  showFullPO = false;
  columnSizes: Map<
    TransactionRowDesktopConstants.ColumnNames,
    string | number
  > = new Map();

  quickFilters: QuickFilter[] = [
    {
      id: [Status.OVERDUE],
      title: 'Overdue',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.overdue',
    },
    {
      id: [Status.UNPAID, Status.UNUSED],
      title: 'Unpaid / Unused',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.unpaidUnused',
    },
    {
      id: [Status.PARTIALLY_PAID],
      title: 'Partially paid',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.partiallyPaid',
    },
  ];
  showOrderNumber: boolean;

  constructor(
    public router: Router,
    readonly route: ActivatedRoute,
    readonly paymentsService: PaymentsService,
    readonly platformService: PlatformService,
    public readonly adjustPaymentService: AdjustPaymentService,
    private readonly panAppState: PanAppState,
    private readonly translateService: TranslateService,
    private readonly invoiceIsrService: InvoiceIsrService,
    private readonly adjustPaymentAnalytics: AdjustPaymentAnalyticsService,
  ) {
    super(router, route, paymentsService);
    this.initSort();
  }

  ionViewWillEnter(): void {
    super.ionViewWillEnter();

    this.vm$ = this.adjustPaymentService.getViewModel$();
    this.browserDimensions = this.platformService.getBrowserDimensions();
    this.isMobile = this.platformService.platformType === PlatformEnum.mobile;
    this.isTouch$ = this.platformService.isTouchExperience$;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.makePayment),
    );
    this.setTranslatedText();

    this.noSelectedTransactionsSub$ = this.vm$
      .pipe(
        filter(vm => vm.pageNum === 0 && !this.isInitialLoading),
        take(1),
      )
      .subscribe(vm => {
        this.adjustPaymentService.setInitialSortAndFilter([], []);
        this.selectedCustomersSub$?.unsubscribe();
      });

    this.selectedCustomersSub$ = this.vm$
      .pipe(
        filter(vm => vm.selectedCustomers.length > 0),
        take(1),
      )
      .subscribe(vm => {
        const excludeInvoices = this.getExcludedInvoices(
          vm.selectedTransactions,
          vm.translatedIds,
        );

        this.adjustPaymentService.setInitialSelectedInvoices();
        this.adjustPaymentService.setInitialSortAndFilter(
          vm.selectedCustomers,
          excludeInvoices,
        );
        this.noSelectedTransactionsSub$?.unsubscribe();
      });

    this.loadingSub$ = this.vm$
      .pipe(
        filter(vm => vm.isLoadingTransaction || vm.pageNum !== 0),
        distinctUntilKeyChanged('isLoadingTransaction'),
      )
      .subscribe(vm => {
        this.isInitialLoading = vm.isLoadingTransaction;
        if (!vm.isLoadingTransaction) {
          this.loadingSub$?.unsubscribe();
        }
      });

    this.getFiltersSub$ = this.adjustPaymentService
      .getFiltersAndSortInformation$()
      .subscribe(res => {
        this.invoiceFilters = res.filters;
      });

    this.selectedTransactionsSub$ = this.vm$
      .pipe(distinctUntilKeyChanged('selectedTransactions'))
      .subscribe(vm => {
        const excludeInvoices = this.getExcludedInvoices(
          vm.selectedTransactions,
          vm.translatedIds,
        );
        this.adjustPaymentService.setExcludeInvoices(excludeInvoices);
      });
    this.validateCustomersWhenSuperUser();
    this.updateColumnSizes();
  }

  ionViewDidEnter(): void {
    const navigationState = history.state;
    if (
      !!navigationState &&
      PaymentConstants.INITIAL_TOAST_ERROR_MESSAGE in navigationState
    ) {
      const initialToastError =
        navigationState[PaymentConstants.INITIAL_TOAST_ERROR_MESSAGE];
      this.vm$
        .pipe(
          filter(vm => vm.selectedTransactions.length > 0),
          take(1),
        )
        .subscribe(vm => {
          if (
            vm.selectedTransactions.length ===
            PaymentConstants.MAX_INVOICE_SELECTION
          ) {
            this.paymentsService.showToastMessage(initialToastError);
          }
        });
    }
  }

  validateCustomersWhenSuperUser(): void {
    this.paymentCustomers$ = combineLatest([
      this.paymentsService.getSelectedPaymentCustomers(),
      this.vm$,
    ])
      .pipe(
        filter(([paymentCustomers]) => paymentCustomers.customers.length > 0),
      )
      .subscribe(([paymentCustomers, vm]) => {
        if (
          paymentCustomers.isSuperUser &&
          !paymentCustomers.customers.some(customer =>
            vm.selectedCustomers.includes(customer),
          )
        ) {
          this.clearAllSelections();
          this.adjustPaymentService.setInitialSortAndFilter(
            paymentCustomers.customers,
            [],
          );
        }
        this.showOrderNumber = vm.showOrderNumber;
        this.updateColumnSizes();
      });
  }

  getExcludedInvoices(
    selectedTransactions: PaymentTransactionsViewModel[],
    translatedIds: Dictionary<string>,
  ) {
    return selectedTransactions
      .map(value => value._id)
      .map(id => (translatedIds[id] != null ? translatedIds[id] : id));
  }

  ionViewWillLeave() {
    this.getFiltersSub$?.unsubscribe();
    this.loadingSub$?.unsubscribe();
    this.noSelectedTransactionsSub$?.unsubscribe();
    this.quickFiltersSub$?.unsubscribe();
    this.paymentCustomers$.unsubscribe();
    this.selectedCustomersSub$?.unsubscribe();
    this.selectedTransactionsSub$?.unsubscribe();
  }

  clearAllSelections() {
    this.paymentsService.clearSelectedInvoices();
  }

  deselectAllSelections() {
    this.clearAllSelections();
    this.adjustPaymentService.setFilter(
      this.invoiceFilters.filter(
        filter => filter.filterName !== InvoiceFilterIds.ExcludeInvoices,
      ),
    );
  }

  goToReviewPayment() {
    this.router.navigate([PATHS.PAYMENTS_REVIEW_PAYMENT]);
  }

  initSort() {
    this.selectedSortDirection =
      this.adjustPaymentService.defaultFilter.sortValue;
    this.selectedSortField = this.adjustPaymentService.defaultFilter.fieldName;
    this.sortDirection = this.adjustPaymentService.defaultFilter.sortValue;
    this.sortField = this.adjustPaymentService.defaultFilter.fieldName;
  }

  paySelectedInvoices(transactions: PaymentTransactionsViewModel[]) {
    if (
      transactions.some(
        transaction =>
          transaction.status === Status.PENDING ||
          transaction.status === Status.PROCESSING ||
          transaction.status === Status.AUTOPAY,
      )
    ) {
      this.warningMessageBanner = this.translateService.instant(
        'i18n.payments.adjustPayment.removeUnpayableTransactions',
      );
      return;
    } else if (
      transactions.some(
        transaction =>
          transaction.paymentAmount < 0.01 &&
          transaction.status !== Status.UNUSED,
      ) ||
      transactions.reduce(
        (sum, transaction) => sum + (transaction.paymentAmount || 0),
        0,
      ) <= 0
    ) {
      this.warningMessageBanner = this.translateService.instant(
        'i18n.payments.adjustPayment.paymentMustBeAboveZero',
      );
      return;
    }

    this.warningMessageBanner = '';
    this.goToReviewPayment();
  }

  resetFilters() {
    this.adjustPaymentService.setInitialSelectedInvoices();
    this.initSort();
  }

  scrolledIndexChange(_event, pageNum: number, hasNextPage: boolean) {
    const end = this.virtualScrollViewport.getRenderedRange().end;
    const total = this.virtualScrollViewport.getDataLength();
    const nextPage = pageNum + 1;

    if (end === total && hasNextPage) {
      this.paymentsService.fetchInvoices(
        nextPage,
        undefined,
        this.adjustPaymentService.restrictToOpen,
      );
    }
  }

  async selectAllInvoices() {
    const vm = await firstValueFrom(this.vm$);
    const unselectedInitialTransactions = vm.initialSelectedTransactions.filter(
      transaction => !transaction.selected,
    );
    const differenceInvoicesToSelect =
      PaymentConstants.MAX_INVOICE_SELECTION - vm.selectedTransactions.length;

    if (
      differenceInvoicesToSelect > 0 &&
      unselectedInitialTransactions.length > 0
    ) {
      this.paymentsService.selectInvoices(
        unselectedInitialTransactions.slice(
          0,
          differenceInvoicesToSelect > unselectedInitialTransactions.length
            ? unselectedInitialTransactions.length
            : differenceInvoicesToSelect,
        ),
      );
    }
    this.paymentsService.selectAllInvoices(
      this.maxTransactionsSelectedMessage,
      {
        restrictToOpen: this.adjustPaymentService.restrictToOpen,
      },
      differenceInvoicesToSelect - unselectedInitialTransactions.length,
    );
  }

  selectTransactionRow(transactionVM: PaymentTransactionsViewModel) {
    this.vm$.pipe(take(1)).subscribe(vm => {
      if (!transactionVM.selected && vm.isMaxTransactionsSelected) {
        this.paymentsService.showToastMessage(
          this.maxTransactionsSelectedMessage,
        );
      } else {
        this.paymentsService.selectInvoice(transactionVM);
      }
    });
  }

  handleSort(
    {
      sortField,
      sortDirection,
    }: { sortField: string; sortDirection: SortDirection },
    isSelectedItems: boolean,
  ) {
    isSelectedItems
      ? this.sortSelected(sortField as SortFields, sortDirection)
      : this.sort(sortField as SortFields, sortDirection);
  }

  sort(field: SortFields, direction: SortDirection = SortDirection.DESC) {
    if (field === this.sortField) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.sortDirection = direction;
    }
    this.sortField = field;

    let sortFields = [{ fieldName: field, sortValue: this.sortDirection }];
    if (this.sortField !== SortFields.transaction) {
      sortFields.push({
        fieldName: SortFields.transaction,
        sortValue: SortDirection.DESC,
      });
    }

    this.generateFilters(this.invoiceFilters).then(newFilters => {
      this.adjustPaymentService.setFilter(newFilters);
      this.adjustPaymentService.setSort(sortFields);
    });
  }

  sortSelected(
    field: SortFields,
    direction: SortDirection = SortDirection.DESC,
  ) {
    if (field === this.selectedSortField) {
      this.selectedSortDirection =
        this.selectedSortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.selectedSortDirection = direction;
    }
    this.selectedSortField = field;

    let selectedSortFields = [
      { fieldName: field, sortValue: this.selectedSortDirection },
    ];
    if (this.selectedSortField !== SortFields.transaction) {
      selectedSortFields.push({
        fieldName: SortFields.transaction,
        sortValue: SortDirection.DESC,
      });
    }

    this.generateFilters(this.invoiceFilters).then(newFilters => {
      this.adjustPaymentService.setFilter(newFilters);
      this.adjustPaymentService.setSelectedSort(selectedSortFields);
    });
  }

  private setTranslatedText() {
    this.translateService
      .get('i18n.payments.invoicesAndCredits.title')
      .pipe(take(1))
      .subscribe(_ => {
        this.transactionRowText = {
          invoice: this.translateService.instant(
            'i18n.payments.transactionRow.invoice',
          ),
          custPo: this.translateService.instant(
            'i18n.payments.transactionRow.custPo',
          ),
          issueDate: this.translateService.instant(
            'i18n.payments.transactionRow.issueDate',
          ),
          issueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.issueDateCut',
          ),
          amount: this.translateService.instant(
            'i18n.payments.transactionRow.amount',
          ),
          dueDate: this.translateService.instant(
            'i18n.payments.transactionRow.dueDate',
          ),
          amountDue: this.translateService.instant(
            'i18n.payments.transactionRow.amountDue',
          ),
          dueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.dueDateCut',
          ),
          amountCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountCut',
          ),
          amountDueCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountDueCut',
          ),
          pay: this.translateService.instant('i18n.common.pay'),
          paymentAmount: this.translateService.instant(
            'i18n.payments.transactionRow.paymentAmount',
          ),
        };
        this.amountIsHigherErrorMessage = this.translateService.instant(
          'i18n.payments.adjustPayment.amountIsHigherForRowsError',
        );
        this.amountIsZeroErrorMessage = this.translateService.instant(
          'i18n.payments.adjustPayment.amountIsZeroError',
        );
        this.maxTransactionsSelectedMessage = this.translateService.instant(
          'i18n.payments.adjustPayment.maximumInvoicesSelected',
          { maximumSelected: PaymentConstants.MAX_INVOICE_SELECTION },
        );
      });
  }

  handleShortPayModal(transaction: PaymentTransactionsViewModel) {
    if (
      !transaction.paymentAmount ||
      transaction.paymentAmount >= transaction.amountDue
    ) {
      this.adjustPaymentService.updateInvoice(transaction, {
        shortPayCode: null,
      });
      return;
    }
    this.adjustPaymentAnalytics.trackOpenShortPayModal();
    this.adjustPaymentService.openShortPayValidationModal().then(resp => {
      if (!!resp.data?.shortPayCode) {
        this.adjustPaymentService.updateInvoice(transaction, {
          shortPayCode: resp.data.shortPayCode,
        });
        this.adjustPaymentAnalytics.trackSubmitShortPayModal(
          resp.data.shortPayCode,
        );
      } else {
        this.adjustPaymentService.updateInvoice(transaction, {
          paymentAmount: transaction.amountDue,
          shortPayCode: null,
        });
        this.adjustPaymentAnalytics.trackCloseShortPayModal();
      }
    });
  }

  handleEditAmount(transaction: PaymentTransactionsViewModel) {
    this.adjustPaymentService
      .openEditAmountValidationModal(transaction)
      .then(({ data }) => {
        if (!!data) {
          this.updatePaymentAmount({ transaction, ...data });
        }
      });
  }

  toggleStatusFilter(newFilters: Filter[]) {
    this.generateFilters(newFilters).then(newFilters => {
      this.adjustPaymentService.setFilter(newFilters);
    });
  }

  updatePaymentAmount({
    paymentAmount,
    transaction,
    errors,
  }: {
    paymentAmount: number;
    transaction: PaymentTransactionsViewModel;
    errors?: ValidationErrors;
  }) {
    this.vm$.pipe(take(1)).subscribe(vm => {
      const selectedIds = vm.selectedTransactions.map(
        transaction => transaction._id,
      );
      if (
        (paymentAmount !== 0 && !selectedIds.includes(transaction._id)) ||
        (paymentAmount === 0 && selectedIds.includes(transaction._id))
      ) {
        this.paymentsService.selectInvoice(transaction);
      }
      this.adjustPaymentService.updateInvoice(transaction, {
        paymentAmount,
      });

      errors
        ? this.displayErrorToast(errors)
        : this.handleShortPayModal({
            ...transaction,
            paymentAmount,
          });
    });
  }

  async viewInvoice(transaction: PaymentTransactionsViewModel) {
    this.invoiceIsrService.viewInvoice(
      transaction.isr.docID.toString(),
      transaction.transactionNumber,
      transaction.secondaryTransactionNumber,
    );
  }

  displayErrorToast(errors) {
    const errorMessage = errors.max
      ? this.amountIsHigherErrorMessage
      : errors.min
        ? this.amountIsZeroErrorMessage
        : '';
    if (errorMessage) {
      this.paymentsService.showToastMessage(
        errorMessage,
        false,
        MessageTypeEnum.warning,
      );
    }
  }

  trackByIdx(i) {
    return i;
  }

  private async generateFilters(invoiceFilters: Filter[]): Promise<Filter[]> {
    const vm = await firstValueFrom(this.vm$);
    const excludeInvoices = this.getExcludedInvoices(
      vm.selectedTransactions,
      vm.translatedIds,
    );

    let newFilters = invoiceFilters.filter(
      invoiceFilter =>
        invoiceFilter.filterName !== InvoiceFilterIds.ExcludeInvoices,
    );

    if (excludeInvoices.length > 0) {
      newFilters.push({
        filterName: InvoiceFilterIds.ExcludeInvoices,
        value: excludeInvoices,
      });
    }

    return newFilters;
  }

  poNumberClicked() {
    this.showFullPO = !this.showFullPO;
    this.updateColumnSizes();
  }

  updateColumnSizes() {
    this.columnSizes = TransactionRowDesktopConstants.columnSizes(
      true,
      false,
      true,
      this.showFullPO,
      this.showOrderNumber,
    );
  }
}
